import React, { useMemo } from "react";
import { volumeTiles, VolTile } from "./Kocampaview";
import { volumeTotals } from "./Kocampaparse";

/* Highlights — the default landing tab.
   Tiles are live (computed from the workbook). The summary below is
   static, pre-written text: edit SUMMARY / KO_SHARE_RS10 to change it. */

const T = {
  surface: "#FFFFFF", border: "#FADDDD", accent: "#E8001A", accentDark: "#B50015",
  accentSoft: "#FFF0F0", text: "#1A0005", textMid: "#5C1020", textDim: "#9A5060",
  pos: "#1A7A3A", neg: "#C42020", shadow: "0 2px 8px rgba(232,0,26,0.08)",
};

/* ── Editable content (KO_SHARE_RS10 also drives the 5th tile) ── */
export const KO_SHARE_RS10 = { label: "KO Share in Rs 10 Segment", metric: "CCI Share",
  year: "2026", value: "20.6%", changeLabel: "Ch vs LY", change: "0.5%", up: true };

/* Rs 10 segment share gain vs LY, last five months */
export const RS10_SHARE_GAIN = [
  ["Apr-26", "1.1%"], ["May-26", "2.3%"], ["Jun-26", "2.5%"], ["Jul-26", "2.9%"], ["Aug-26", "2.4%"],
];

export const SUMMARY = {
  title: "Executive Summary: Affordable Portfolio Performance",
  intro: "The Affordable segment represents steadily growing share of total volume, trending from 2.77% in January to 5.03% in August — nearly doubling its contribution over the eight-month period.",
  takeaways: [
    ["📈", "Affordable contribution growth is high", null],   // value filled from data
    ["🌍", "Broad Market Reach:", "The portfolio is distributed across 46–49 business units, showing wide geographic deployment."],
    ["🔝", "Peak Absolute Volume", "was recorded in June at 12.88 million units, coinciding with the peak in total market volume (316.9M)."],
  ],
  implications: [
    ["Affordable is outpacing the broader portfolio", "— its share is rising even as total volumes fluctuate, signaling strong underlying demand."],
    ["Room to grow", "— at ~5% share, there is significant headroom. Expanding the 8-brand portfolio or increasing distribution intensity could accelerate growth further."],
  ],
  bottomLine: "The Affordable segment is on a clear upward trajectory and proving its resilience. It warrants continued investment and potential portfolio expansion to capture incremental volume in an increasingly value-conscious market.",
};

const H = ({ children }) => (
  <div style={{ color: T.accentDark, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
    textTransform: "uppercase", margin: "18px 0 8px" }}>{children}</div>
);

export default function Highlights({ koCampa, cci10 }) {
  const month = koCampa?.months?.[koCampa.months.length - 1] || "";
  const totals = useMemo(
    () => (koCampa ? volumeTotals(koCampa, month, "ytd") : null),
    [koCampa, month]
  );

  const volTiles = totals
    ? volumeTiles(totals).filter(t => ["koAff", "affMix", "contribution"].includes(t.id))
    : [];

  const contribTxt = totals?.contribution != null
    ? `— ${(totals.contribution * 100).toFixed(0)}% of incremental KO volume (${totals.curLabel} vs ${totals.prevLabel}) came from Affordable PET.`
    : "";

  return (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Tiles ── */}
      <div className="ddr-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
        {cci10 && (
          <VolTile label="Aff CCI@10 (Distribution)" value={cci10.value} color={T.accent}
            icon="🏪" sub={cci10.month ? `outlets · ${cci10.month}` : undefined}
            delta={cci10.delta} deltaLabel={cci10.deltaLabel} />
        )}
        {volTiles.map(k => <VolTile key={k.id} {...k} />)}
        <VolTile label={KO_SHARE_RS10.label} value={KO_SHARE_RS10.value} color="#1A7A3A"
          icon="🎯" sub={`${KO_SHARE_RS10.metric} · ${KO_SHARE_RS10.year}`}
          deltaPts={(KO_SHARE_RS10.up ? 1 : -1) * parseFloat(KO_SHARE_RS10.change) / 100}
          deltaLabel="vs LY" />
      </div>

      {/* ── Summary ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12,
        padding: "18px 22px", boxShadow: T.shadow }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Summary</span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: "#5B2FA0",
            background: "#EEE8F8", border: "1px solid #D8CCF0", borderRadius: 20,
            padding: "2px 9px" }}>✨ AI Generated</span>
        </div>

        <div style={{ fontSize: 15, fontWeight: 700, color: T.accentDark, marginTop: 14 }}>{SUMMARY.title}</div>
        <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, margin: "8px 0 0" }}>{SUMMARY.intro}</p>

        <H>Key Takeaways</H>
        <ul style={{ margin: 0, paddingLeft: 4, listStyle: "none" }}>
          <li style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
            📊 <b>{KO_SHARE_RS10.label}:</b> {KO_SHARE_RS10.metric} {KO_SHARE_RS10.value} in {KO_SHARE_RS10.year}, {KO_SHARE_RS10.up ? "up" : "down"} {KO_SHARE_RS10.change} vs last year.
          </li>
          <li style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
            🚀 <b>Gaining share for the Rs 10 price segment</b> for the last {RS10_SHARE_GAIN.length} months:
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "6px 0 2px 22px" }}>
              {RS10_SHARE_GAIN.map(([m, v]) => (
                <span key={m} style={{ border: `1px solid ${T.border}`, borderRadius: 6,
                  padding: "3px 9px", fontSize: 11.5, background: "#F3FAF5" }}>
                  <span style={{ color: T.textDim }}>{m}</span>{" "}
                  <b style={{ color: T.pos }}>▲ {v}</b>
                </span>
              ))}
            </div>
          </li>
          {SUMMARY.takeaways.map(([ic, b, rest], i) => (
            <li key={i} style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
              {ic} <b>{b}</b> {rest ?? contribTxt}
            </li>
          ))}
        </ul>

        <H>Strategic Implications</H>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {SUMMARY.implications.map(([b, rest], i) => (
            <li key={i} style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
              <b>{b}</b> {rest}
            </li>
          ))}
        </ol>

        <div style={{ marginTop: 16, padding: "12px 16px", background: T.accentSoft,
          borderLeft: `4px solid ${T.accent}`, borderRadius: 6 }}>
          <b style={{ color: T.accentDark, fontSize: 13 }}>Bottom Line: </b>
          <span style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>{SUMMARY.bottomLine}</span>
        </div>
      </div>
    </div>
  );
}